import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  collection, query, where, getDocs, orderBy, doc,
  updateDoc, onSnapshot, addDoc
} from "firebase/firestore";
import { db } from "../firebase-config";
import html2pdf from "html2pdf.js";
import emailjs from '@emailjs/browser';
import {
  Dialog, DialogContent, DialogTitle, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Card, CardContent,
  LinearProgress, TextField, IconButton, Tooltip, Typography, Button,
  Box, Select, MenuItem, FormControl, InputLabel, alpha, Chip, Stack,
  Divider, useMediaQuery, InputAdornment, Grid, Collapse, Fade,
  CircularProgress, Snackbar, Alert, styled, Switch, FormControlLabel
} from "@mui/material";
import {
  Search, Download, ChevronRight, X, ArrowLeft, Calendar,
  Building2, Mail, Receipt, CreditCard, Printer, Filter,
  ChevronDown, ChevronUp, Eye, Send, CheckCircle, Warning, Tag
} from "lucide-react";
import { Phone, Business, Info } from "@mui/icons-material";
import Header from "../components/header";
import LOGO from "../assets/MF-CPU-LOGO.png";
import muiTheme from "../theme/muiTheme";
import moment from "moment";

// **STYLED COMPONENTS WITH MF THEME**
const PageContainer = styled(Box)({
  padding: muiTheme.spacing(3),
  backgroundColor: muiTheme.palette.background.default,
  minHeight: "100vh",
});

const StyledCard = styled(Card)({
  borderRadius: muiTheme.shape.borderRadius,
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
  border: `1px solid ${alpha(muiTheme.palette.primary.main, 0.1)}`,
  transition: "all 0.3s ease",
  backgroundColor: muiTheme.palette.background.paper,
  "&:hover": {
    boxShadow: "0 8px 30px rgba(0, 0, 0, 0.12)",
    transform: "translateY(-2px)",
  }
});

const HeaderCard = styled(StyledCard)({
  background: `linear-gradient(135deg, ${muiTheme.palette.primary.main} 0%, ${muiTheme.palette.secondary.main} 100%)`,
  color: "white",
  marginBottom: muiTheme.spacing(4),
});

const SearchField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: muiTheme.shape.borderRadius,
    backgroundColor: muiTheme.palette.background.paper,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
    "& fieldset": {
      borderColor: alpha(muiTheme.palette.primary.main, 0.2),
    },
    "&:hover fieldset": {
      borderColor: muiTheme.palette.primary.main,
    },
    "&.Mui-focused fieldset": {
      borderColor: muiTheme.palette.primary.main,
      borderWidth: "2px",
    },
  },
});

const ActionButton = styled(Button)(({ variant, color }) => ({
  borderRadius: muiTheme.shape.borderRadius,
  textTransform: "none",
  fontWeight: 600,
  padding: "8px 20px",
  boxShadow: variant === "contained" ? "0 4px 12px rgba(0, 0, 0, 0.1)" : "none",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: variant === "contained" ? "0 6px 16px rgba(0, 0, 0, 0.15)" : "0 2px 8px rgba(0, 0, 0, 0.1)",
  }
}));

const StyledTableContainer = styled(TableContainer)({
  borderRadius: muiTheme.shape.borderRadius,
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
  border: `1px solid ${alpha(muiTheme.palette.primary.main, 0.1)}`,
  backgroundColor: muiTheme.palette.background.paper,
  overflow: "hidden",
});

const StyledTableHead = styled(TableHead)({
  background: `linear-gradient(135deg, ${muiTheme.palette.primary.main} 0%, #E53935 100%)`,
  "& .MuiTableCell-head": {
    color: "white",
    fontWeight: 600,
    fontSize: "0.875rem",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: "none",
    padding: "16px",
  }
});

const StatusChip = styled(Chip)(({ status }) => ({
  fontWeight: 600,
  borderRadius: "20px",
  ...(status === "Paid" && {
    backgroundColor: alpha(muiTheme.palette.success.main, 0.1),
    color: muiTheme.palette.success.main,
    border: `1px solid ${alpha(muiTheme.palette.success.main, 0.3)}`,
  }),
  ...(status === "Pending" && {
    backgroundColor: alpha(muiTheme.palette.warning.main, 0.1),
    color: muiTheme.palette.warning.main,
    border: `1px solid ${alpha(muiTheme.palette.warning.main, 0.3)}`,
  }),
  ...(status === "Overdue" && {
    backgroundColor: alpha(muiTheme.palette.error.main, 0.1),
    color: muiTheme.palette.error.main,
    border: `1px solid ${alpha(muiTheme.palette.error.main, 0.3)}`,
  }),
}));

const ConsolidatedInvoiceView = () => {
  // **STATE MANAGEMENT**
  const [restaurants, setRestaurants] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [consolidatedInvoices, setConsolidatedInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoiceGroup, setSelectedInvoiceGroup] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState("Pending");
  const [isPrinting, setIsPrinting] = useState(false);
  const [expandedPeriods, setExpandedPeriods] = useState({});
  const [activeFilter, setActiveFilter] = useState("all");
  const [periodType, setPeriodType] = useState('fortnight');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [showDiscountColumns, setShowDiscountColumns] = useState(false);
  const [rowDiscounts, setRowDiscounts] = useState({});
  const [rowSaveStatus, setRowSaveStatus] = useState({});
  const [editingReason, setEditingReason] = useState({});

  // **EMAIL FUNCTIONALITY STATE**
  const [emailSending, setEmailSending] = useState(false);
  const [emailStatus, setEmailStatus] = useState({ show: false, message: "", type: "success" });
  const [emailDialog, setEmailDialog] = useState({ open: false, email: "", message: "" });

  const invoiceRef = useRef(null);
  const isMobile = useMediaQuery("(max-width:600px)");

  // **SAFE HELPERS**
  const safeToFixed = (value, decimals = 2) => {
    if (value === undefined || value === null || isNaN(value)) return '0.00';
    return Number(value).toFixed(decimals);
  };

  const calculateDiscountedTotal = (originalTotal = 0, discType = 'none', discValue = '') => {
    if (discType === 'none' || !discValue) return originalTotal;
    const discount = parseFloat(discValue) || 0;
    if (discType === 'percent') return originalTotal * (1 - discount / 100);
    return originalTotal - discount;
  };

  const safeNumber = (value) => {
    if (value === undefined || value === null || isNaN(value)) return 0;
    return Number(value);
  };

  // **DATE FORMATTING FUNCTIONS**
  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return moment(date).format("MMM D, YYYY");
  };

  // **CHECK IF GROUP HAS DISCOUNTS**
  const checkIfGroupHasDiscounts = (invoices) => {
    if (!invoices || !Array.isArray(invoices)) return false;
    return invoices.some(inv => 
      inv.discountType && inv.discountType !== 'none' && 
      inv.discountValue && parseFloat(inv.discountValue) > 0
    );
  };

  // **FILTER INVOICES BY PERIOD**
  const filterInvoicesByPeriod = (invoices, periodType, fromDate, toDate) => {
    if (!Array.isArray(invoices) || invoices.length === 0) return [];

    if (periodType === 'custom' && fromDate && toDate) {
      const start = new Date(fromDate);
      const end = new Date(toDate);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);

      return invoices.filter(inv => {
        const ts = inv.createdAt;
        const date = ts?.toDate ? ts.toDate() : new Date(ts);
        return date >= start && date <= end;
      });
    }

    if (periodType === 'month') {
      const now = new Date();
      const targetYear = now.getFullYear();
      const targetMonth = now.getMonth();

      return invoices.filter(inv => {
        const ts = inv.createdAt;
        const date = ts?.toDate ? ts.toDate() : new Date(ts);
        return date.getFullYear() === targetYear && date.getMonth() === targetMonth;
      });
    }

    // Default: fortnight logic (existing 1-15 / 16-end)
    return invoices;
  };

  // **BUILD CONSOLIDATED INVOICES**
  const buildConsolidatedInvoices = (invoices, periodType, fromDate, toDate) => {
    const filtered = filterInvoicesByPeriod(invoices, periodType, fromDate, toDate);

    const grouped = {};

    filtered.forEach(inv => {
      const ts = inv.createdAt;
      const date = ts?.toDate ? ts.toDate() : new Date(ts);
      const monthName = moment(date).format('MMMM');
      const year = date.getFullYear();
      const userId = inv.userId;
      const day = date.getDate();
      
      // **FORTNIGHT LOGIC: 1-15 / 16-end**
      let periodKey, periodLabel, periodDisplay, startOfPeriod, endOfPeriod;

      if (periodType === 'fortnight') {
        const period = day <= 15 ? 'first' : 'second';
        periodKey = `${year}-${monthName}-${period}`;
        periodLabel = day <= 15 ? '1st - 15th' : '16th - End';
        periodDisplay = periodLabel;
        
        // Set period boundaries
        if (day <= 15) {
          startOfPeriod = new Date(year, date.getMonth(), 1);
          endOfPeriod = new Date(year, date.getMonth(), 15);
        } else {
          startOfPeriod = new Date(year, date.getMonth(), 16);
          endOfPeriod = new Date(year, date.getMonth() + 1, 0);
        }
      } 
      else if (periodType === 'month') {
        startOfPeriod = moment(date).startOf('month').toDate();
        endOfPeriod = moment(date).endOf('month').toDate();
        periodKey = `${year}-${monthName}`;
        periodLabel = `${monthName} ${year}`;
        periodDisplay = periodLabel;
      }
      else if (periodType === 'custom' && fromDate && toDate) {
        startOfPeriod = moment(fromDate).startOf('day').toDate();
        endOfPeriod = moment(toDate).endOf('day').toDate();
        periodKey = `${moment(fromDate).format('YYYY-MM-DD')}_${moment(toDate).format('YYYY-MM-DD')}`;
        periodLabel = `${moment(fromDate).format('D MMM YYYY')} - ${moment(toDate).format('D MMM YYYY')}`;
        periodDisplay = periodLabel;
      }

      const key = `${userId}-${periodKey}`;

      if (!grouped[key]) {
        grouped[key] = {
          userId,
          year,
          month: monthName,
          period: periodKey,
          periodLabel: periodDisplay,
          periodType: periodType === 'fortnight' ? (day <= 15 ? '1-15' : '16-end') : periodLabel,
          invoices: [],
          totalAmount: 0,
          totalDiscountedAmount: 0,
          totalItems: 0,
          startDate: startOfPeriod,
          endDate: endOfPeriod,
          paymentStatus: 'Pending',
          hasDiscounts: false,
        };
      }

      const g = grouped[key];
      g.invoices.push(inv);
      
      const invoiceTotal = safeNumber(inv.totalPrice);
      g.totalAmount += invoiceTotal;
      
      // Calculate discounted amount if applicable
      if (inv.discountType && inv.discountType !== 'none' && inv.discountValue) {
        const discounted = calculateDiscountedTotal(invoiceTotal, inv.discountType, inv.discountValue);
        g.totalDiscountedAmount += discounted;
        g.hasDiscounts = true;
      } else {
        g.totalDiscountedAmount += invoiceTotal;
      }
      
      g.totalItems += inv.items?.length || 0;

      // Update payment status
      const status = inv.orderStatus || inv.paymentStatus || 'Pending';
      if (status === 'Paid' && g.paymentStatus === 'Pending') {
        // Keep as Pending until all paid
      } else if (status !== 'Paid') {
        g.paymentStatus = 'Pending';
      }
    });

    const result = Object.values(grouped).map(g => ({
      ...g,
      periodText: `${formatDate(g.startDate)} - ${formatDate(g.endDate)}`,
      // Use discounted amount if available, otherwise use total amount
      displayAmount: g.hasDiscounts ? g.totalDiscountedAmount : g.totalAmount,
    }));

    // Sort by endDate (newest first)
    result.sort((a, b) => b.endDate - a.endDate);
    return result;
  };

  // **SEND EMAIL FUNCTION**
  const sendConsolidatedInvoice = async (customEmail, message = "") => {
    if (!selectedInvoiceGroup || !selectedRestaurant) {
      setEmailStatus({
        show: true,
        message: "No invoice selected to send",
        type: "error"
      });
      return;
    }

    const recipientEmail = customEmail || selectedRestaurant.email;
    if (!recipientEmail) {
      setEmailStatus({
        show: true,
        message: "No customer email found",
        type: "error"
      });
      return;
    }

    setEmailSending(true);
    try {
      emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY);
      
      const invoiceNumber = `MF-${selectedInvoiceGroup.year}-${selectedInvoiceGroup.month.slice(0, 3).toUpperCase()}-${selectedInvoiceGroup.periodType === "1-15" ? "A" : "B"}`;
      const totalAmount = selectedInvoiceGroup.hasDiscounts 
        ? safeToFixed(selectedInvoiceGroup.totalDiscountedAmount)
        : safeToFixed(selectedInvoiceGroup.totalAmount);
      
      const emailParams = {
        to_email: recipientEmail,
        to_name: selectedRestaurant.restaurantName,
        subject: `📧 Consolidated Invoice ${invoiceNumber} - MF Kitchen`,
        invoice_number: invoiceNumber,
        total_amount: totalAmount,
        billing_period: `${formatDate(selectedInvoiceGroup.startDate)} - ${formatDate(selectedInvoiceGroup.endDate)}`,
        restaurant_name: selectedRestaurant.restaurantName,
        has_discounts: selectedInvoiceGroup.hasDiscounts,
        original_amount: selectedInvoiceGroup.hasDiscounts ? safeToFixed(selectedInvoiceGroup.totalAmount) : null,
        discount_amount: selectedInvoiceGroup.hasDiscounts 
          ? safeToFixed(selectedInvoiceGroup.totalAmount - selectedInvoiceGroup.totalDiscountedAmount)
          : null,
      };

      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        emailParams
      );

      // Log email send
      await addDoc(collection(db, "emailLogs"), {
        type: "consolidated_invoice",
        restaurantId: selectedRestaurant.id,
        restaurantName: selectedRestaurant.restaurantName,
        recipientEmail: recipientEmail,
        invoiceNumber: invoiceNumber,
        totalAmount: totalAmount,
        sentAt: new Date().toISOString(),
        status: "sent"
      });

      setEmailStatus({
        show: true,
        message: `📧 Invoice sent to ${recipientEmail}!`,
        type: "success"
      });
      setEmailDialog({ open: false, email: "", message: "" });
    } catch (error) {
      console.error("Error sending email:", error);
      setEmailStatus({
        show: true,
        message: `❌ Failed to send invoice`,
        type: "error"
      });
    } finally {
      setEmailSending(false);
    }
  };

  // **ENHANCED PDF GENERATION**
  const handleDownloadPDF = async () => {
    setIsPrinting(true);
    try {
      const element = invoiceRef.current;
      if (!element) return;

      const clone = element.cloneNode(true);

      const hideElements = clone.querySelectorAll('.hide-in-pdf');
    hideElements.forEach(el => {
      // If it's a table cell, also hide its parent row's cell
      if (el.tagName === 'TD' || el.tagName === 'TH') {
        el.style.display = 'none';
      } else {
        el.parentNode?.removeChild(el);
      }
    });
      
      // Ensure total row stays together
      const totalRow = clone.querySelector('.total-row');
      if (totalRow) {
        totalRow.style.breakInside = 'avoid';
        totalRow.style.pageBreakInside = 'avoid';
      }

      // Create a wrapper to ensure table doesn't break awkwardly
      const tables = clone.querySelectorAll('table');
      tables.forEach(table => {
        table.style.breakInside = 'avoid';
        table.style.pageBreakInside = 'avoid';
      });

      const options = {
        margin: [10, 10, 10, 10],
        filename: `consolidated-invoice-${selectedRestaurant?.restaurantName || 'details'}-${new Date().toISOString().slice(0, 10)}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          windowWidth: showDiscountColumns ? 1600 : 1200,
          windowHeight: 1600,
          onclone: (clonedDoc) => {
            // Ensure all elements are visible for PDF
            const discountElements = clonedDoc.querySelectorAll('.discount-column');
            discountElements.forEach(el => {
              if (!showDiscountColumns) {
                el.style.display = 'none';
              }
            });
            
            // Add page break prevention
            const allTables = clonedDoc.querySelectorAll('table');
            allTables.forEach(table => {
              table.style.breakInside = 'avoid';
              table.style.pageBreakInside = 'avoid';
            });
            
            const totalRows = clonedDoc.querySelectorAll('.total-row');
            // totalRows.forEach(row => {
            //   row.style.breakInside = 'avoid';
            //   row.style.pageBreakInside = 'avoid';
            //   row.style.display = 'table-row-group';
            // });
          }
        },
        jsPDF: {
          unit: 'mm',
          format: showDiscountColumns ? 'a4' : 'a4',
          orientation: showDiscountColumns ? 'landscape' : 'portrait',
        },
      };

      await new Promise(resolve => setTimeout(resolve, 500));
      await html2pdf().set(options).from(clone).save();
    } catch (error) {
      console.error("PDF generation error:", error);
    } finally {
      setIsPrinting(false);
    }
  };

  // **ROW DISCOUNT FUNCTIONS**
  const updateRowDiscount = (invoiceId, field, value) => {
    setRowDiscounts(prev => ({
      ...prev,
      [invoiceId]: { ...(prev[invoiceId] || { type: 'none', value: '', reason: '' }), [field]: value }
    }));
  };

  const saveRowDiscount = async (invoice) => {
    const rowDisc = rowDiscounts[invoice.id] || { type: 'none', value: '', reason: '' };
    
    try {
      setRowSaveStatus(prev => ({ ...prev, [invoice.id]: 'saving' }));

      const invoiceDocRef = doc(db, 'invoices', invoice.id);
      const discountedTotal = calculateDiscountedTotal(
        invoice.totalPrice || 0,
        rowDisc.type,
        rowDisc.value
      );
      
      const updates = {
        discountType: rowDisc.type === 'none' ? null : rowDisc.type,
        discountValue: rowDisc.type === 'none' ? null : parseFloat(rowDisc.value) || 0,
        discountReason: rowDisc.type === 'none' ? null : rowDisc.reason,
        discountedTotal: discountedTotal
      };

      await updateDoc(invoiceDocRef, updates);

      // Update the selected invoice group state
      setSelectedInvoiceGroup(prev => {
        const updatedInvoices = prev.invoices.map(inv =>
          inv.id === invoice.id
            ? { ...inv, ...updates }
            : inv
        );
        
        // Recalculate group totals
        let totalAmount = 0;
        let totalDiscountedAmount = 0;
        let hasDiscounts = false;
        
        updatedInvoices.forEach(inv => {
          totalAmount += safeNumber(inv.totalPrice);
          
          if (inv.discountType && inv.discountType !== 'none' && inv.discountValue) {
            const discounted = calculateDiscountedTotal(inv.totalPrice, inv.discountType, inv.discountValue);
            totalDiscountedAmount += discounted;
            hasDiscounts = true;
          } else {
            totalDiscountedAmount += safeNumber(inv.totalPrice);
          }
        });
        
        return {
          ...prev,
          invoices: updatedInvoices,
          totalAmount,
          totalDiscountedAmount,
          hasDiscounts,
          displayAmount: hasDiscounts ? totalDiscountedAmount : totalAmount
        };
      });

      setRowSaveStatus(prev => ({ ...prev, [invoice.id]: 'saved' }));

      setTimeout(() => {
        setRowSaveStatus(prev => {
          const copy = { ...prev };
          delete copy[invoice.id];
          return copy;
        });
      }, 2000);
    } catch (err) {
      console.error('Row discount save failed', err);
      setRowSaveStatus(prev => ({ ...prev, [invoice.id]: 'error' }));
    }
  };

  // **PAYMENT STATUS UPDATE**
  const handlePaymentStatusChange = async (newStatus) => {
    setPaymentStatus(newStatus);
    const isPaid = newStatus === "Paid";

    try {
      await Promise.all(
        selectedInvoiceGroup.invoices.map((invoice) => {
          const invoiceDocRef = doc(db, "invoices", invoice.id);
          return updateDoc(invoiceDocRef, { isBillPaid: isPaid });
        })
      );

      setSelectedInvoiceGroup((prev) => ({
        ...prev,
        invoices: prev.invoices.map((invoice) => ({
          ...invoice,
          isBillPaid: isPaid,
        })),
        paymentStatus: newStatus,
      }));
    } catch (error) {
      console.error("Error updating payment status: ", error);
    }
  };

  // **FETCH RESTAURANTS**
  const fetchRestaurants = async () => {
    try {
      const usersRef = collection(db, "users");
      const querySnapshot = await getDocs(usersRef);
      const restaurantData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setRestaurants(restaurantData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching restaurants:", error);
      setLoading(false);
    }
  };

  // **FETCH INVOICES**
  const fetchInvoices = (restaurantId) => {
    const invoicesRef = collection(db, "invoices");
    const q = query(
      invoicesRef,
      where("userId", "==", restaurantId),
      orderBy("createdAt", "asc")
    );

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const allInvoices = querySnapshot.docs.map(doc => {
          const data = doc.data();
          if (typeof data.createdAt === 'string') {
            data.createdAt = new Date(data.createdAt);
          }
          return {
            id: doc.id,
            ...data
          };
        });

        const groups = buildConsolidatedInvoices(
          allInvoices,
          periodType,
          fromDate,
          toDate
        );
        setConsolidatedInvoices(groups);

        // Prepare row discounts cache
        const rowCache = {};
        let hasAnyDiscounts = false;
        
        allInvoices.forEach(inv => {
          if (inv.discountType && inv.discountType !== 'none' && inv.discountValue) {
            rowCache[inv.id] = {
              type: inv.discountType,
              value: inv.discountValue ?? '',
              reason: inv.discountReason ?? '',
            };
            hasAnyDiscounts = true;
          }
        });
        
        setRowDiscounts(rowCache);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching invoices:", error);
        setLoading(false);
      }
    );

    return unsubscribe;
  };

  // **TOGGLE PERIOD EXPANSION**
  const togglePeriodExpansion = (periodKey) => {
    setExpandedPeriods((prev) => ({
      ...prev,
      [periodKey]: !prev[periodKey],
    }));
  };

  // **FILTERED INVOICES**
  const filteredInvoices = consolidatedInvoices.filter((group) => {
    if (activeFilter === "all") return true;
    if (activeFilter === "paid") return group.paymentStatus === "Paid";
    return group.paymentStatus === "Pending";
  });

  // **FILTERED RESTAURANTS**
  const filteredRestaurants = restaurants.filter((restaurant) =>
    restaurant.restaurantName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // **CALCULATE GROUP TOTALS**
  const groupTotals = useMemo(() => {
    if (!selectedInvoiceGroup) {
      return { items: 0, original: 0, discounted: 0 };
    }

    let totalItems = 0;
    let originalTotal = 0;
    let discountedTotal = 0;

    selectedInvoiceGroup.invoices.forEach(inv => {
      totalItems += inv.items?.length || 0;
      originalTotal += inv.totalPrice || 0;
      
      const rowDisc = rowDiscounts[inv.id] || { type: 'none', value: 0 };
      discountedTotal += calculateDiscountedTotal(
        inv.totalPrice || 0,
        rowDisc.type,
        rowDisc.value
      );
    });

    return {
      items: totalItems,
      original: originalTotal,
      discounted: discountedTotal,
    };
  }, [selectedInvoiceGroup?.invoices, rowDiscounts]);

  // **TRUNCATE TEXT**
  const truncateText = (text = '', length = 20) => {
    if (!text) return '';
    return text.length > length ? text.slice(0, length) + '…' : text;
  };

  // **MODAL HANDLERS**
  const handleOpenModal = (group) => {
    setSelectedInvoiceGroup(group);
    setPaymentStatus(group.paymentStatus);
    
    // Check if group has discounts and set toggle accordingly
    const hasDiscounts = checkIfGroupHasDiscounts(group.invoices);
    setShowDiscountColumns(hasDiscounts);
    
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedInvoiceGroup(null);
  };

  const handleBackToRestaurants = () => {
    setSelectedRestaurant(null);
    setConsolidatedInvoices([]);
    setSelectedInvoiceGroup(null);
    setShowModal(false);
    setShowDiscountColumns(false);
  };

  // **EFFECTS**
  useEffect(() => {
    fetchRestaurants();
  }, []);

  useEffect(() => {
    if (selectedRestaurant?.id) {
      setLoading(true);
      const unsubscribe = fetchInvoices(selectedRestaurant.id);
      return () => unsubscribe();
    }
  }, [selectedRestaurant, periodType, fromDate, toDate]);

  return (
    <PageContainer>
      <Header title="Consolidated Invoices" />

      {/* SUCCESS/ERROR SNACKBAR */}
      <Snackbar
        open={emailStatus.show}
        autoHideDuration={6000}
        onClose={() => setEmailStatus({ ...emailStatus, show: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setEmailStatus({ ...emailStatus, show: false })}
          severity={emailStatus.type}
          sx={{ borderRadius: 8 }}
        >
          {emailStatus.message}
        </Alert>
      </Snackbar>

      {/* EMAIL SEND DIALOG */}
      <Dialog
        open={emailDialog.open}
        onClose={() => !emailSending && setEmailDialog({ open: false, email: "", message: "" })}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2, // Reduced border radius
          }
        }}
      >
        <DialogTitle sx={{ color: muiTheme.palette.primary.main, fontWeight: 'bold' }}>
          <Send /> Send Invoice to Customer
        </DialogTitle>
        <DialogContent>
          <Box sx={{ marginTop: 2 }}>
            <TextField
              fullWidth
              label="Customer Email"
              value={emailDialog.email}
              onChange={(e) => setEmailDialog(prev => ({ ...prev, email: e.target.value }))}
              placeholder={selectedRestaurant?.email || "Enter customer email"}
              helperText={`Default: ${selectedRestaurant?.email || 'No email found'}`}
              margin="normal"
            />
          </Box>
        </DialogContent>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', padding: 2 }}>
          <ActionButton onClick={() => setEmailDialog({ open: false, email: "", message: "" })}>
            Cancel
          </ActionButton>
          <ActionButton
            onClick={() => sendConsolidatedInvoice(emailDialog.email, emailDialog.message)}
            disabled={emailSending}
            variant="contained"
            sx={{
              backgroundColor: muiTheme.palette.primary.main,
              '&:hover': { backgroundColor: muiTheme.palette.primary.dark }
            }}
          >
            {emailSending ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: 'white' }} />
                Sending...
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Send size={16} />
                Send to Customer
              </Box>
            )}
          </ActionButton>
        </Box>
      </Dialog>

      {/* MAIN CONTENT */}
      {!selectedRestaurant ? (
        // RESTAURANT SELECTION VIEW
        <Fade in={!selectedRestaurant}>
          <div>
            <HeaderCard sx={{ padding: 4, borderRadius: 2 }}> {/* Reduced radius */}
              <Typography variant="h4" sx={{
                fontWeight: 700,
                marginBottom: 1,
              }}>
                🏢 Consolidated Invoice Management
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9 }}>
                Select a restaurant to view and manage consolidated invoices
              </Typography>
            </HeaderCard>

            <StyledCard sx={{ marginBottom: 4 }}>
              <CardContent sx={{ padding: 3 }}>
                <Box sx={{
                  display: 'flex',
                  gap: 3,
                  alignItems: 'center',
                  flexWrap: { xs: 'wrap', md: 'nowrap' }
                }}>
                  <Box sx={{ flex: 1, minWidth: 300 }}>
                    <SearchField
                      fullWidth
                      placeholder="Search restaurants by name..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search sx={{ color: muiTheme.palette.text.secondary }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  <Box sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'center',
                    flexWrap: 'wrap'
                  }}>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel id="period-type-label">Period</InputLabel>
                      <Select
                        labelId="period-type-label"
                        value={periodType}
                        label="Period"
                        onChange={(e) => setPeriodType(e.target.value)}
                      >
                        <MenuItem value="fortnight">1st - 15th / 16th - End</MenuItem>
                        <MenuItem value="month">Full Month</MenuItem>
                        <MenuItem value="custom">Custom Range</MenuItem>
                      </Select>
                    </FormControl>

                    {periodType === 'custom' && (
                      <>
                        <TextField
                          label="From date"
                          type="date"
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          value={fromDate ? moment(fromDate).format('YYYY-MM-DD') : ''}
                          onChange={(e) => setFromDate(e.target.value ? new Date(e.target.value) : null)}
                        />
                        <TextField
                          label="To date"
                          type="date"
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          value={toDate ? moment(toDate).format('YYYY-MM-DD') : ''}
                          onChange={(e) => setToDate(e.target.value ? new Date(e.target.value) : null)}
                        />
                      </>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </StyledCard>

            <StyledTableContainer>
              <Table>
                <StyledTableHead>
                  <TableRow>
                    <TableCell>Restaurant</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </StyledTableHead>
                <TableBody>
                  {filteredRestaurants.map((restaurant) => (
                    <TableRow
                      key={restaurant.id}
                      sx={{
                        "&:hover": {
                          backgroundColor: alpha(muiTheme.palette.primary.main, 0.05),
                        }
                      }}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle1" sx={{
                            fontWeight: 600,
                            color: muiTheme.palette.text.primary,
                          }}>
                            {restaurant.restaurantName}
                          </Typography>
                          <Typography variant="body2" sx={{
                            color: muiTheme.palette.text.secondary,
                          }}>
                            ID: {restaurant.id}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {restaurant.address || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Phone fontSize="small" sx={{ color: muiTheme.palette.text.secondary }} />
                            {restaurant.phone || 'N/A'}
                          </Typography>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Mail size={16} color={muiTheme.palette.text.secondary} />
                            {restaurant.email || 'N/A'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <ActionButton
                          variant="contained"
                          onClick={() => {
                            setSelectedRestaurant(restaurant);
                            fetchInvoices(restaurant.id);
                          }}
                          sx={{
                            backgroundColor: muiTheme.palette.primary.main,
                            '&:hover': {
                              backgroundColor: muiTheme.palette.primary.dark,
                            },
                          }}
                        >
                          View Invoices
                        </ActionButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          </div>
        </Fade>
      ) : (
        // INVOICE PERIODS VIEW
        <Fade in={!!selectedRestaurant}>
          <div>
            <HeaderCard sx={{ padding: 3, borderRadius: 2 }}> {/* Reduced radius */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <IconButton
                    onClick={handleBackToRestaurants}
                    sx={{
                      color: 'inherit',
                      backgroundColor: alpha('#FFFFFF', 0.1),
                      '&:hover': {
                        backgroundColor: alpha('#FFFFFF', 0.2),
                      },
                    }}
                  >
                    <ArrowLeft />
                  </IconButton>
                  <Box>
                    <Typography variant="h5" sx={{
                      fontWeight: 700,
                      marginBottom: 0.5,
                    }}>
                      📊 {selectedRestaurant.restaurantName}
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9 }}>
                      Consolidated invoice periods and billing history
                    </Typography>
                  </Box>
                </Box>

                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel sx={{ color: 'inherit' }}>Filter</InputLabel>
                  <Select
                    value={activeFilter}
                    onChange={(e) => setActiveFilter(e.target.value)}
                    label="Filter"
                    sx={{
                      color: 'inherit',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha('#FFFFFF', 0.3),
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha('#FFFFFF', 0.5),
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha('#FFFFFF', 0.7),
                      },
                    }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="paid">Paid</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </HeaderCard>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', padding: 6 }}>
                <CircularProgress size={50} sx={{ color: muiTheme.palette.primary.main }} />
              </Box>
            ) : (
              <StyledTableContainer>
                <Table>
                  <StyledTableHead>
                    <TableRow>
                      <TableCell>Month & Year</TableCell>
                      <TableCell>Period</TableCell>
                      <TableCell>Invoices</TableCell>
                      <TableCell>Total Items</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </StyledTableHead>
                  <TableBody>
                    {filteredInvoices.map((group) => {
                      const periodKey = `${group.year}-${group.month}-${group.period}`;
                      const isExpanded = expandedPeriods[periodKey];

                      return (
                        <React.Fragment key={periodKey}>
                          <TableRow
                            sx={{
                              "&:hover": {
                                backgroundColor: alpha(muiTheme.palette.primary.main, 0.05),
                              }
                            }}
                          >
                            <TableCell>
                              <Typography variant="subtitle1" sx={{
                                fontWeight: 600,
                              }}>
                                {group.month} {group.year}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ color: muiTheme.palette.text.secondary }}>
                                {group.periodLabel}
                              </Typography>
                              <Typography variant="body2" sx={{ color: muiTheme.palette.text.secondary, fontSize: '0.75rem' }}>
                                {formatDate(group.startDate)} - {formatDate(group.endDate)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={group.invoices.length}
                                sx={{
                                  backgroundColor: alpha(muiTheme.palette.primary.main, 0.1),
                                  color: muiTheme.palette.primary.main,
                                  fontWeight: 600,
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {group.totalItems}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="subtitle1" sx={{
                                fontWeight: 600,
                                color: muiTheme.palette.success.main,
                              }}>
                                £{safeToFixed(group.displayAmount)}
                              </Typography>
                              {group.hasDiscounts && (
                                <Typography variant="caption" sx={{
                                  color: muiTheme.palette.warning.main,
                                  fontStyle: 'italic',
                                  display: 'block',
                                }}>
                                  (Includes discounts)
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <StatusChip
                                label={group.paymentStatus}
                                status={group.paymentStatus}
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title="View Details">
                                  <IconButton
                                    onClick={() => handleOpenModal(group)}
                                  >
                                    <Eye size={18} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Toggle Details">
                                  <IconButton
                                    onClick={() => togglePeriodExpansion(periodKey)}
                                    sx={{
                                      color: muiTheme.palette.secondary.main,
                                      backgroundColor: alpha(muiTheme.palette.secondary.main, 0.1),
                                      '&:hover': {
                                        backgroundColor: alpha(muiTheme.palette.secondary.main, 0.2),
                                      },
                                    }}
                                  >
                                    {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>

                          {/* EXPANDED DETAILS */}
                          <TableRow>
                            <TableCell colSpan={7} sx={{ padding: 0, border: 'none' }}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{
                                  margin: 2,
                                  padding: 3,
                                  backgroundColor: alpha(muiTheme.palette.primary.light, 0.3),
                                  borderRadius: muiTheme.shape.borderRadius,
                                  border: `1px solid ${alpha(muiTheme.palette.primary.main, 0.1)}`,
                                }}>
                                  <Typography variant="h6" sx={{
                                    marginBottom: 2,
                                    color: muiTheme.palette.primary.main,
                                    fontWeight: 600,
                                  }}>
                                    📋 Individual Invoices
                                  </Typography>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Invoice #</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Items</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {group.invoices.map((invoice) => (
                                        <TableRow
                                          key={invoice.id}
                                          sx={{
                                            "&:hover": {
                                              backgroundColor: alpha(muiTheme.palette.primary.main, 0.05),
                                            }
                                          }}
                                        >
                                          <TableCell>
                                            <Typography variant="body2" sx={{
                                              fontFamily: 'monospace',
                                              color: muiTheme.palette.primary.main,
                                              fontWeight: 600,
                                            }}>
                                              #{invoice.id.slice(0, 8)}...
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Typography variant="body2">
                                              {formatDate(invoice.createdAt)}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Typography variant="body2">
                                              {invoice.items?.length || 0}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Typography variant="body2" sx={{
                                              fontWeight: 600,
                                              color: muiTheme.palette.success.main,
                                            }}>
                                              £{safeToFixed(invoice.totalPrice)}
                                            </Typography>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </StyledTableContainer>
            )}
          </div>
        </Fade>
      )}

      {/* INVOICE DETAILS MODAL */}
      <Dialog
        open={showModal}
        onClose={handleCloseModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2, // Reduced border radius
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle sx={{
          background: `linear-gradient(135deg, ${muiTheme.palette.primary.main} 0%, ${muiTheme.palette.secondary.main} 100%)`,
          color: "white",
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: 3,
        }}>
          <Typography variant="h5" sx={{
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}>
            Consolidated Invoice
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showDiscountColumns}
                  onChange={(e) => setShowDiscountColumns(e.target.checked)}
                  color="secondary"
                  size="small"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Tag size={16} />
                  <span>Show Discounts</span>
                </Box>
              }
              sx={{ color: 'white', marginRight: 2 }}
            />
            <IconButton
              onClick={handleCloseModal}
              sx={{
                color: 'inherit',
                backgroundColor: alpha('#FFFFFF', 0.1),
                '&:hover': {
                  backgroundColor: alpha('#FFFFFF', 0.2),
                },
              }}
            >
              <X />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ padding: 0 }}>
          {selectedInvoiceGroup && (
            <Box ref={invoiceRef} sx={{ padding: 4 }}>
              {/* BILL FROM/TO SECTION */}
              <Grid container spacing={3} sx={{ marginBottom: 4 }}>
                <Grid item xs={12} md={6}>
                  <StyledCard sx={{
                    background: `linear-gradient(135deg, ${muiTheme.palette.primary.light} 0%, #FFFFFF 100%)`,
                    border: `2px solid ${muiTheme.palette.primary.main}`,
                  }}>
                    <CardContent>
                      <Typography variant="h6" sx={{
                        color: muiTheme.palette.primary.main,
                        fontWeight: 700,
                        marginBottom: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}>
                        <Building2 size={20} /> Bill From:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, marginBottom: 1 }}>
                        Central Kitchen
                      </Typography>
                      <Typography variant="body2" sx={{ color: muiTheme.palette.text.secondary }}>
                        UNIT 5, 152 High Street, Hounslow, TW3 1LR
                      </Typography>
                    </CardContent>
                  </StyledCard>
                </Grid>

                <Grid item xs={12} md={6}>
                  <StyledCard sx={{
                    background: `linear-gradient(135deg, ${muiTheme.palette.secondary.light} 0%, #FFFFFF 100%)`,
                    border: `2px solid ${muiTheme.palette.secondary.main}`,
                  }}>
                    <CardContent>
                      <Typography variant="h6" sx={{
                        color: muiTheme.palette.secondary.main,
                        fontWeight: 700,
                        marginBottom: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}>
                        <Business size={20} /> Bill To:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, marginBottom: 1 }}>
                        {selectedRestaurant.restaurantName}
                      </Typography>
                      <Typography variant="body2" sx={{ color: muiTheme.palette.text.secondary, marginBottom: 0.5 }}>
                        📞 {selectedRestaurant.phone || 'N/A'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: muiTheme.palette.text.secondary, marginBottom: 0.5 }}>
                        📧 {selectedRestaurant.email || 'N/A'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: muiTheme.palette.text.secondary }}>
                        📍 {selectedRestaurant.address || 'N/A'}
                      </Typography>
                    </CardContent>
                  </StyledCard>
                </Grid>
              </Grid>

              {/* INVOICE DETAILS */}
              <StyledCard sx={{
                marginBottom: 4,
                background: `linear-gradient(135deg, ${muiTheme.palette.primary.light} 0%, ${muiTheme.palette.secondary.light} 100%)`,
                border: `2px solid ${muiTheme.palette.warning.main}`,
              }}>
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          Invoice Period:
                        </Typography>
                        <Typography variant="body1">
                          {formatDate(selectedInvoiceGroup.startDate)} - {formatDate(selectedInvoiceGroup.endDate)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          Invoice Number:
                        </Typography>
                        <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                          MF-{selectedInvoiceGroup.year}-{selectedInvoiceGroup.month.slice(0, 3).toUpperCase()}-{selectedInvoiceGroup.periodType === "1-15" ? "A" : "B"}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          Total Amount:
                        </Typography>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="h6" sx={{
                            color: muiTheme.palette.success.main,
                            fontWeight: 700,
                          }}>
                            £{safeToFixed(selectedInvoiceGroup.displayAmount, 2)}
                          </Typography>
                          {selectedInvoiceGroup.hasDiscounts && selectedInvoiceGroup.totalAmount !== selectedInvoiceGroup.totalDiscountedAmount && (
                            <Typography variant="caption" sx={{
                              color: muiTheme.palette.warning.main,
                              fontStyle: 'italic',
                            }}>
                              Original: £{safeToFixed(selectedInvoiceGroup.totalAmount, 2)}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          Payment Status:
                        </Typography>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <Select
                            value={paymentStatus}
                            onChange={(e) => handlePaymentStatusChange(e.target.value)}
                            sx={{
                              borderRadius: muiTheme.shape.borderRadius,
                            }}
                          >
                            <MenuItem value="Pending">Pending</MenuItem>
                            <MenuItem value="Paid">Paid</MenuItem>
                            <MenuItem value="Overdue">Overdue</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </StyledCard>

              {/* INVOICE TABLE WITH TOGGLEABLE DISCOUNT COLUMNS */}
              <StyledTableContainer>
                <Table>
                  <StyledTableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Invoice</TableCell>
                      <TableCell>Items</TableCell>
                      {showDiscountColumns && (
                        <>
                          <TableCell className="discount-column">Disc. Type</TableCell>
                          <TableCell className="discount-column">Value</TableCell>
                          <TableCell className="discount-column">Reason</TableCell>
                          <TableCell className="discount-column">Original</TableCell>
                        </>
                      )}
                      <TableCell sx={{ fontWeight: 'bold' }}>Final Amount</TableCell>
                      {showDiscountColumns && <TableCell className="discount-column hide-in-pdf">Action</TableCell>}
                    </TableRow>
                  </StyledTableHead>
                  <TableBody>
                    {[...selectedInvoiceGroup.invoices]
                      .sort((a, b) => {
                        const da = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
                        const db = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
                        return da - db;
                      }).map((invoice) => {
                        const rowDisc = rowDiscounts[invoice.id] || {
                          type: 'none',
                          value: 0,
                          reason: ''
                        };

                        const rowDiscountedTotal = calculateDiscountedTotal(
                          invoice.totalPrice || 0,
                          rowDisc.type,
                          rowDisc.value
                        );

                        return (
                          <TableRow key={invoice.id} hover>
                            <TableCell>{formatDate(invoice.createdAt)}</TableCell>
                            <TableCell>{invoice.id.slice(0, 8)}...</TableCell>
                            <TableCell>{invoice.items?.length || 0}</TableCell>
                            
                            {showDiscountColumns && (
                              <>
                                <TableCell className="discount-column">
                                  <FormControl size="small" sx={{ minWidth: 80 }}>
                                    <Select
                                      value={rowDisc.type}
                                      onChange={(e) => updateRowDiscount(invoice.id, 'type', e.target.value)}
                                    >
                                      <MenuItem value="none">None</MenuItem>
                                      <MenuItem value="percent">%</MenuItem>
                                      <MenuItem value="amount">£</MenuItem>
                                    </Select>
                                  </FormControl>
                                </TableCell>
                                <TableCell className="discount-column">
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={rowDisc.type === 'none' ? 0 : rowDisc.value}
                                    onChange={(e) => updateRowDiscount(invoice.id, 'value', e.target.value || 0)}
                                    disabled={rowDisc.type === 'none'}
                                    sx={{ width: 70 }}
                                  />
                                </TableCell>
                                <TableCell className="discount-column">
                                  {editingReason[invoice.id] ? (
                                    <TextField
                                      size="small"
                                      value={rowDisc.reason}
                                      onChange={(e) => updateRowDiscount(invoice.id, 'reason', e.target.value)}
                                      onBlur={() => setEditingReason(prev => ({ ...prev, [invoice.id]: false }))}
                                      autoFocus
                                      sx={{ width: 120 }}
                                    />
                                  ) : rowDisc.reason ? (
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                      <Typography
                                        variant="body2"
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => setEditingReason(prev => ({ ...prev, [invoice.id]: true }))}
                                      >
                                        {truncateText(rowDisc.reason, 16)}
                                      </Typography>
                                      <Tooltip title={rowDisc.reason}>
                                        <Info fontSize="small" sx={{ color: 'gray', cursor: 'pointer' }} />
                                      </Tooltip>
                                    </Stack>
                                  ) : (
                                    <Button
                                      size="small"
                                      variant="text"
                                      onClick={() => setEditingReason(prev => ({ ...prev, [invoice.id]: true }))}
                                    >
                                      Add reason
                                    </Button>
                                  )}
                                </TableCell>
                                <TableCell className="discount-column">
                                  £{safeToFixed(invoice.totalPrice)}
                                </TableCell>
                              </>
                            )}
                            
                            <TableCell sx={{ fontWeight: 'bold', color: 'green' }}>
                              £{safeToFixed(rowDiscountedTotal)}
                            </TableCell>
                            
                            {showDiscountColumns && (
                              <TableCell className="discount-column hide-in-pdf">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => saveRowDiscount(invoice)}
                                  disabled={rowSaveStatus[invoice.id] === 'saving'}
                                >
                                  {rowSaveStatus[invoice.id] === 'saving'
                                    ? 'Saving...'
                                    : rowSaveStatus[invoice.id] === 'saved'
                                    ? 'Saved ✓'
                                    : 'Save'}
                                </Button>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    
                    {/* TOTAL ROW - Fixed to prevent page breaks */}
                    <TableRow className="total-row" sx={{ 
                      backgroundColor: alpha(muiTheme.palette.primary.main, 0.08),
                    }}>
                      <TableCell colSpan={showDiscountColumns ? 2 : 2} sx={{ fontWeight: 700 }}>
                        TOTAL
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {groupTotals.items}
                      </TableCell>
                      
                      {showDiscountColumns && (
                        <>
                          <TableCell className="discount-column" colSpan={3} />
                          <TableCell className="discount-column" sx={{ fontWeight: 700 }}>
                            £{safeToFixed(groupTotals.original)}
                          </TableCell>
                        </>
                      )}
                      
                      <TableCell sx={{ fontWeight: 700, color: muiTheme.palette.success.main }}>
                        £{safeToFixed(groupTotals.discounted)}
                      </TableCell>
                      
                      {showDiscountColumns && <TableCell className="discount-column" />}
                    </TableRow>
                  </TableBody>
                </Table>
              </StyledTableContainer>
            </Box>
          )}
        </DialogContent>

        {/* MODAL ACTIONS */}
        <Box sx={{
          display: 'flex',
          gap: 2,
          justifyContent: 'flex-end',
          padding: 3,
          backgroundColor: muiTheme.palette.background.paper,
          borderTop: `1px solid ${alpha(muiTheme.palette.primary.main, 0.1)}`,
        }}>
          {/* Send Email Button */}
          {/* <ActionButton
            onClick={() => setEmailDialog({
              open: true,
              email: selectedRestaurant?.email || "",
              message: "",
            })}
            variant="contained"
            disabled={emailSending}
            sx={{
              backgroundColor: muiTheme.palette.secondary.main,
              '&:hover': { backgroundColor: muiTheme.palette.secondary.dark }
            }}
          >
            <Send size={18} style={{ marginRight: 8 }} />
            Send to Customer
          </ActionButton> */}

          {/* Download PDF Button */}
          <ActionButton
            onClick={handleDownloadPDF}
            variant="contained"
            disabled={isPrinting}
            sx={{
              backgroundColor: muiTheme.palette.primary.main,
              '&:hover': { backgroundColor: muiTheme.palette.primary.dark }
            }}
          >
            {isPrinting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: 'white' }} />
                Generating...
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Download size={18} />
                Download PDF
              </Box>
            )}
          </ActionButton>
        </Box>
      </Dialog>
    </PageContainer>
  );
};

export default ConsolidatedInvoiceView;